import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import styles from "./AlumniRegistrationStep.module.css";
import { ArrowRight } from "lucide-react";

interface AlumniFormData {
  studentNumber: string;
  graduationYear: string;
  dateOfBirth: string;
  regenesysQualification: string;
  currentOccupation: string;
  companyName: string;
}

interface AlumniRegistrationStepProps {
  onSubmit: (data: AlumniFormData, isSkipped?: boolean) => Promise<void>;
  isSubmitting: boolean;
  AlumniFormEnable?: boolean;
  data: any;
}

const AlumniRegistrationStep: React.FC<AlumniRegistrationStepProps> = ({
  onSubmit,
  isSubmitting,
  AlumniFormEnable,
  data,
}) => {
  const RequiredField = AlumniFormEnable;
  const [isFormValid, setIsFormValid] = useState(false);
  const [isIOS, setIsIOS] = useState<boolean>(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [hasSelectedDate, setHasSelectedDate] = useState(false);

  useEffect(() => {
    // Detect iOS devices
    const isIOSDevice =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(isIOSDevice);
  }, []);

  // Calculate max date (18 years ago from today)
  const today = new Date();
  const maxDate = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  const maxDateString = maxDate.toISOString().split("T")[0];

  // Calculate min date (100 years ago from today)
  const minDate = new Date(
    today.getFullYear() - 100,
    today.getMonth(),
    today.getDate()
  );
  const minDateString = minDate.toISOString().split("T")[0];

  const formatDateForDisplay = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };


  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<AlumniFormData>({
    mode: "onChange",
  });

  const formValues = watch();

  useEffect(() => {
    if (RequiredField) {
      setIsFormValid(isValid);
    } else {
      // If not required, check if any field has value
      const hasAnyValue = Object.values(formValues).some(
        (value) => value && value.trim() !== ""
      );
      setIsFormValid(hasAnyValue);
    }
  }, [formValues, RequiredField, isValid]);

  const handleFormSubmit = async (data: AlumniFormData) => {
    await onSubmit(data);
  };

  const handleSkip = () => {
    // Submit empty form when skipping with isSkipped flag
    onSubmit(
      {
        studentNumber: "",
        graduationYear: "",
        dateOfBirth: "",
        regenesysQualification: "",
        currentOccupation: "",
        companyName: "",
      },
      true
    );
  };

  const validateDateOfBirth = (value: string) => {
    if (!value) return RequiredField ? "Date of Birth is required" : true;

    const birthDate = new Date(value);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    // Check if birthday has occurred this year
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    if (age < 18) {
      return "You must be at least 18 years old";
    }

    if (birthDate < minDate) {
      return "Date of birth cannot be more than 100 years ago";
    }

    return true;
  };

  const allowOnlyAlphanumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isAlphanumeric = /^[a-zA-Z0-9\s]$/.test(e.key);
    const isControlKey = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Tab",
      "Delete",
      "Space",
    ].includes(e.key);

    if (!isAlphanumeric && !isControlKey) {
      e.preventDefault();
    }
  };

  return (
    <div className={styles.popupContainer}>
      <div className={styles.header}>
        <span>{data?.title || "Alumni Information"}</span>
      </div>
      <div className={styles.formSection}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className={styles.form}>
          <div className={styles.field}>
            <input
              {...register("studentNumber", {
                required: RequiredField ? "Student Number is required" : false,
                maxLength: {
                  value: 50,
                  message: "Student Number cannot exceed 30 characters",
                },
              })}
              placeholder="Student Number"
              autoComplete="off"
              maxLength={50}
            />
          </div>
          <div className={styles.field}>
            <input
              {...register("graduationYear", {
                required: RequiredField ? "Graduation Year is required" : false,
                min: {
                  value: 1900,
                  message: "Graduation Year must be after 1900",
                },
                max: {
                  value: new Date().getFullYear(),
                  message: `Graduation Year cannot be after ${new Date().getFullYear()}`,
                },
              })}
              placeholder="Graduation Year"
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              autoComplete="off"
            />
          </div>
          <div className={styles.field}>
            <div
              className={styles.dateInputWrapper}
              onClick={() => {
                const dateInput = document.querySelector(
                  `.${styles.dateInput}`
                ) as HTMLInputElement;
                if (dateInput) {
                  dateInput.showPicker();
                }
              }}
            >
              <input
                {...register("dateOfBirth", {
                  required: RequiredField ? "Date of Birth is required" : false,
                  validate: validateDateOfBirth,
                })}
                className={`${styles.dateInput} ${
                  isIOS ? styles.iosDateInput : ""
                } ${
                  showPlaceholder && !hasSelectedDate
                    ? styles.showPlaceholder
                    : ""
                }`}
                type="date"
                min={minDateString}
                max={maxDateString}
                autoComplete="off"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    setHasSelectedDate(true);
                    setShowPlaceholder(false);
                    const formattedDate = formatDateForDisplay(value);
                    e.target.setAttribute("data-formatted", formattedDate);
                  } else {
                    setHasSelectedDate(false);
                    setShowPlaceholder(true);
                    e.target.removeAttribute("data-formatted");
                  }
                  setValue("dateOfBirth", value, {
                    shouldValidate: true,
                  });
                }}
                onBlur={(e) => {
                  if (!e.target.value) {
                    setHasSelectedDate(false);
                    setShowPlaceholder(true);
                  }
                  if (e.target.value) {
                    validateDateOfBirth(e.target.value);
                  }
                }}
                style={{ cursor: "pointer" }}
              />
              {showPlaceholder && !hasSelectedDate && (
                <div className={styles.datePlaceholder}>Date of Birth</div>
              )}
              {hasSelectedDate && (
                <div className={styles.formattedDate}>
                  {formatDateForDisplay(watch("dateOfBirth") || "")}
                </div>
              )}
            </div>
          </div>
          <div className={styles.field}>
            <input
              {...register("regenesysQualification", {
                required: RequiredField
                  ? "Most Recent Regenesys Qualification is required"
                  : false,
                pattern: {
                  value: /^[a-zA-Z0-9\s-]*$/,
                  message:
                    "Qualification: Only letters, numbers, spaces and hyphens are allowed",
                },
                maxLength: {
                  value: 50,
                  message: "Qualification cannot exceed 50 characters",
                },
              })}
              placeholder="Most Recent Regenesys Qualification"
              autoComplete="off"
              maxLength={50}
              onKeyDown={allowOnlyAlphanumeric}
            />
          </div>
          <div className={styles.field}>
            <input
              {...register("currentOccupation", {
                required: RequiredField
                  ? "Current Occupation is required"
                  : false,
                pattern: {
                  value: /^[a-zA-Z0-9\s-]*$/,
                  message:
                    "Occupation: Only letters, numbers, spaces and hyphens are allowed",
                },
                maxLength: {
                  value: 50,
                  message: "Occupation cannot exceed 50 characters",
                },
              })}
              placeholder="Current Occupation"
              autoComplete="off"
              maxLength={50}
              onKeyDown={allowOnlyAlphanumeric}
            />
          </div>
          <div className={styles.field}>
            <input
              {...register("companyName", {
                required: RequiredField ? "Company Name is required" : false,
                pattern: {
                  value: /^[a-zA-Z0-9\s-]*$/,
                  message:
                    "Company Name: Only letters, numbers, spaces and hyphens are allowed",
                },
                maxLength: {
                  value: 50,
                  message: "Company Name cannot exceed 50 characters",
                },
              })}
              placeholder="Company Name"
              autoComplete="off"
              maxLength={50}
              onKeyDown={allowOnlyAlphanumeric}
            />

            {!RequiredField && (
              <div className={styles.skipButtonContainer}>
                <button
                  type="button"
                  onClick={handleSkip}
                  className={styles.skipButton}
                >
                  Skip <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>

          {(() => {
            if (RequiredField) {
              // First check if there are any validation errors (like pattern, min, max, etc.)
              const validationErrors = Object.entries(errors).filter(
                ([, error]) => error.type !== "required"
              );

              if (validationErrors.length > 0) {
                // Show the first validation error
                return (
                  <p className={styles.error}>
                    {validationErrors[0][1].message}
                  </p>
                );
              }

              // Then check for required field errors
              const requiredErrors = Object.entries(errors).filter(
                ([, error]) => error.type === "required"
              );

              if (requiredErrors.length > 0) {
                if (requiredErrors.length === Object.keys(errors).length) {
                  // Only show "all fields required" if all errors are required field errors
                  return (
                    <p className={styles.error}>
                      Required: All fields must be filled
                    </p>
                  );
                } else {
                  // Show the first required field error
                  return (
                    <p className={styles.error}>
                      {requiredErrors[0][1].message}
                    </p>
                  );
                }
              }
            } else {
              // Show validation errors even for non-required fields
              const validationErrors = Object.entries(errors).filter(
                ([fieldName, error]) => {
                  return (
                    [
                      "regenesysQualification",
                      "currentOccupation",
                      "companyName",
                    ].includes(fieldName) && error.type === "pattern"
                  );
                }
              );

              if (validationErrors.length > 0) {
                return (
                  <p className={styles.error}>
                    {validationErrors[0][1].message}
                  </p>
                );
              }
            }
            return null;
          })()}

          <div className={styles.buttonWrapper}>
            <button
              type="submit"
              disabled={isSubmitting || (!RequiredField && !isFormValid)}
              className={styles.button}
            >
              {isSubmitting ? "Submitting..." : data?.cta || "Register"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AlumniRegistrationStep;
